import React, { useEffect } from "react";
import { createContext, useState } from "react";
import { isStaticSite, isCloudSite, storage, isElectronApp } from "../electron-modules";
import { useAuth } from "../hooks";
import { ControlType } from "../types/controls";
import { VolumeType, SamplingRate } from "../types/measurements";

interface GlobalSettingsState {
  orthoQuality: number;
  orthoOpacity: number;
  controlType: ControlType;
  openQuickNav: boolean;
  volumeType: VolumeType;
  volumeSampleRate: SamplingRate;
  viewerBackground: string;
  aerialBackground: string;
}

export const initialState = {
  orthoQuality: 0.75,
  orthoOpacity: 1,
  controlType: ControlType.Encompass,
  openQuickNav: true,
  volumeType: VolumeType.MultiPlane,
  volumeSampleRate: SamplingRate.Medium,
  viewerBackground: "#e2d1c3",
  aerialBackground: "#444444"
} as GlobalSettingsState;

export const GlobalSettingsContext = createContext({
  ...initialState,
  update: (values: GlobalSettingsState) => {}
});

export const getGlobalSettingsFromStorage = async () => {
  if (isElectronApp) {
    return storage.getSync("global-settings");
  }

  const cache = Date.now();
  const root = isCloudSite ? window.location.pathname : ".";
  const configURL = `${root}/global-settings.json?cache=${cache}`;

  const response = await fetch(configURL);
  return await response.json();
};

export const GlobalSettingsProvider = (props) => {
  const [state, setState] = useState({...initialState});
  const { initialized } = useAuth();

  const loadFromStorage = async () => {
    try {
      const settings = await getGlobalSettingsFromStorage();
      const newState = {...state, ...settings};

      setState(newState);
    } catch {
      console.error("Error reading global settings");
    }
  };

  const saveToStorage = (values: GlobalSettingsState) => {
    if (isStaticSite) return;
    storage.setSync("global-settings", values);
  };

  const onUpdate = (values: GlobalSettingsState) => {
    setState({...values});
    saveToStorage(values);
  };

  useEffect(() => {
    if (!initialized) return;

    loadFromStorage();
  }, [initialized]);

  return (
    <GlobalSettingsContext.Provider value={{
      ...state,
      update: onUpdate
    }}>
      {props.children}
    </GlobalSettingsContext.Provider>
  );
};